import type { DocumentRecord } from '@/types/catalog'
import { statBucketForDocument } from './catalogStats'

/**
 * FS-601 — reconciling the two writers of a catalog row.
 *
 * A row is written from two places that are not ordered relative to each other:
 *
 *  1. the SSE handler, which patches the row the instant a worker announces a
 *     stage change, and
 *  2. `fetchDocuments`, which replaces the row wholesale from a full-list GET.
 *
 * The GET is scheduled 100ms after the *per-page* `pnid_processing_completed`
 * event — but `pnid_worker` emits that event BEFORE it writes `is_indexed=true`
 * (`pnid_worker.py:1196` vs `:1320`) and before `processing_complete` persists
 * `status='success'`, and `PNID_PROCESSING_COMPLETED` is deliberately excluded
 * from `SUCCESS_EVENT_TYPES` (`internal_events.py:38-47`) so nothing is written
 * on it at all. The response is therefore guaranteed to be built from a
 * pre-completion snapshot. When it lands after the `processing_complete` event
 * that SSE already applied, an unguarded overwrite reverts a finished drawing
 * to "Processing" — and nothing refetches again, so it stays there.
 *
 * These helpers make writer 2 refuse to undo writer 1, using
 * `statBucketForDocument` so "did this row finish?" stays defined in exactly
 * one place (`getDocumentStatus`).
 */

/**
 * The `sse_settled` value a row should carry after an SSE patch.
 *
 * Only meaningful for patches that actually carry status information — callers
 * must skip it for unrelated patches (a rename), otherwise renaming a row that
 * happens to be displaying Success would claim SSE had settled it.
 *
 * `failed` deliberately leaves the flag untouched: a server row that reports
 * failure always wins the merge below, so the flag's value is irrelevant there
 * and preserving it keeps a transient failure event from dropping the guard.
 */
/**
 * Terminal success statuses — the row is done because a worker SAID so, not
 * because a display shortcut inferred it. Mirrors the backend's
 * `_TERMINAL_STATUSES` success half (`document_status_utils.py`).
 */
const TERMINAL_SUCCESS_STATUSES: ReadonlySet<string> = new Set([
  'success',
  'processing_complete',
])

export function nextSseSettled(
  previous: DocumentRecord,
  merged: DocumentRecord
): boolean | undefined {
  const bucket = statBucketForDocument(merged)
  // Settle only on an EARNED success. `getDocumentStatus` renders Success from
  // several shortcuts, one of which (PRIORITY 3, "is a drawing and has a
  // graph_id") can fire while the document is still parsing. Latching the guard
  // onto that would pin a premature "Success" in place instead of letting the
  // next fetch correct it — turning a flicker into a permanent lie. Require the
  // document-level signal the backend actually sets.
  if (bucket === 'processed') {
    const earned =
      merged.is_indexed === true ||
      (merged.status != null && TERMINAL_SUCCESS_STATUSES.has(merged.status))
    return earned ? true : previous.sse_settled
  }
  // A fresh run (retry / re-upload) re-opens the row: drop the guard so the
  // server is free to drive it again.
  if (bucket === 'processing') return false
  return previous.sse_settled
}

/**
 * Merge one server row over the locally-held row.
 *
 * Takes the server as the source of truth EXCEPT where doing so would undo a
 * completion SSE has already delivered — see the module comment. A server row
 * that reports failure or success is always applied; only a "still processing"
 * server row is treated as possibly-stale.
 *
 * Known limit: if another client reprocesses the file and this tab never
 * receives the corresponding `*_started` event, this tab holds Success until
 * the next reload instead of following the server back to Processing. That is
 * deliberate — the alternative is the bug this exists to fix, which mislabels
 * every ordinary completion — and it self-corrects as soon as any `*_started`
 * event or a page load arrives.
 */
export function mergeServerDocumentRow(
  serverDoc: DocumentRecord,
  existing: DocumentRecord | undefined
): DocumentRecord {
  if (!existing) return serverDoc

  // `processing_metadata` and `sse_settled` are frontend-only — the API never
  // returns them, so a plain overwrite drops them and makes
  // `hasProcessingDocuments` miscalculate.
  const merged: DocumentRecord = { ...serverDoc, sse_settled: existing.sse_settled }
  if (existing.processing_metadata) {
    merged.processing_metadata = existing.processing_metadata
  }

  if (!existing.sse_settled) return merged
  if (statBucketForDocument(serverDoc) !== 'processing') return merged

  // SSE says this row finished and this response says it is still processing,
  // so the response predates the completion. Keep the finished state.
  return {
    ...merged,
    status: existing.status,
    is_indexed: existing.is_indexed,
    // Restore a graph_id the snapshot hadn't been given yet; otherwise leave
    // the server's properties alone (it holds the newer page-progress map).
    properties:
      existing.properties?.graph_id && !serverDoc.properties?.graph_id
        ? { ...serverDoc.properties, graph_id: existing.properties.graph_id }
        : serverDoc.properties,
  }
}
